import { useState, useEffect } from 'react';
import { useWidget } from '../WidgetProvider';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Card } from '@/components/ui/card';
import { Checkbox } from '@/components/ui/checkbox';
import { User, Mail, Shield, Loader2 } from 'lucide-react';

const DEFAULT_OPT_IN_TEXT = "I'd like to receive design inspiration, tips, and special offers via email. You can unsubscribe anytime.";

export function ContactStep() {
  const { state, updateContact, nextStep, isLoading, trackEvent, tenantConfig, createLead } = useWidget();
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [isCreating, setIsCreating] = useState(false);

  // Email opt-in configuration from tenant settings
  const emailOptInEnabled = tenantConfig?.widget?.email_opt_in_enabled !== false;
  const emailOptInText = tenantConfig?.widget?.email_opt_in_text || DEFAULT_OPT_IN_TEXT;
  const emailOptInDefault = tenantConfig?.widget?.email_opt_in_default ?? false; // GDPR: default unchecked

  // Initialize opt-in state from config default (only once on mount)
  useEffect(() => {
    if (state.contact.email_opt_in === undefined && emailOptInEnabled) {
      updateContact({ email_opt_in: emailOptInDefault });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []); // Intentionally empty - only run on mount to set initial value

  const validateEmail = (email: string): boolean => {
    const regex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return regex.test(email);
  };

  const validateForm = (): boolean => {
    const newErrors: Record<string, string> = {};

    if (!state.contact.first_name.trim()) {
      newErrors.first_name = 'First name is required';
    }

    if (!state.contact.last_name.trim()) {
      newErrors.last_name = 'Last name is required';
    }

    if (!state.contact.email.trim()) {
      newErrors.email = 'Email is required';
    } else if (!validateEmail(state.contact.email)) {
      newErrors.email = 'Please enter a valid email';
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (validateForm()) {
      setIsCreating(true);
      try {
        await createLead();
        trackEvent('contact_submitted');
        nextStep();
      } catch (err) {
        console.error('Failed to create lead:', err);
        // Continue to next step even if lead creation fails
        nextStep();
      } finally {
        setIsCreating(false);
      }
    }
  };

  return (
    <div className="space-y-4">
      <div className="text-center mb-6">
        <h2 className="text-lg font-medium">Almost there!</h2>
        <p className="text-sm text-muted-foreground mt-1">
          Enter your details to receive your AI renovation preview
        </p>
      </div>

      <form onSubmit={handleSubmit} className="space-y-4">
        {/* Name Fields */}
        <div className="grid grid-cols-2 gap-3">
          <div className="space-y-2">
            <Label htmlFor="first_name">First Name</Label>
            <div className="relative">
              <User className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
              <Input
                id="first_name"
                value={state.contact.first_name}
                onChange={(e) => {
                  updateContact({ first_name: e.target.value });
                  if (errors.first_name) {
                    setErrors((prev) => ({ ...prev, first_name: '' }));
                  }
                }}
                placeholder="John"
                className="pl-9"
              />
            </div>
            {errors.first_name && (
              <p className="text-xs text-destructive">{errors.first_name}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="last_name">Last Name</Label>
            <Input
              id="last_name"
              value={state.contact.last_name}
              onChange={(e) => {
                updateContact({ last_name: e.target.value });
                if (errors.last_name) {
                  setErrors((prev) => ({ ...prev, last_name: '' }));
                }
              }}
              placeholder="Doe"
            />
            {errors.last_name && (
              <p className="text-xs text-destructive">{errors.last_name}</p>
            )}
          </div>
        </div>

        {/* Email */}
        <div className="space-y-2">
          <Label htmlFor="email">Email Address</Label>
          <div className="relative">
            <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-muted-foreground" />
            <Input
              id="email"
              type="email"
              value={state.contact.email}
              onChange={(e) => {
                updateContact({ email: e.target.value });
                if (errors.email) {
                  setErrors((prev) => ({ ...prev, email: '' }));
                }
              }}
              placeholder="john@example.com"
              className="pl-9"
            />
          </div>
          {errors.email && (
            <p className="text-xs text-destructive">{errors.email}</p>
          )}
        </div>

        {/* Email Marketing Opt-in (GDPR Compliant) */}
        {emailOptInEnabled && (
          <div className="flex items-start gap-3 py-2">
            <Checkbox
              id="email_opt_in"
              checked={state.contact.email_opt_in ?? false}
              onCheckedChange={(checked) => {
                updateContact({
                  email_opt_in: checked === true,
                  // Record timestamp when user opts in (for compliance)
                  email_opt_in_timestamp: checked === true ? new Date().toISOString() : undefined,
                });
              }}
              className="mt-0.5"
            />
            <Label
              htmlFor="email_opt_in"
              className="text-sm text-muted-foreground font-normal leading-relaxed cursor-pointer"
            >
              {emailOptInText}
            </Label>
          </div>
        )}

        {/* Privacy Notice */}
        <Card className="p-4 bg-muted/50">
          <div className="flex gap-3">
            <Shield className="w-5 h-5 text-muted-foreground shrink-0 mt-0.5" />
            <div className="text-xs text-muted-foreground">
              <p className="font-medium text-foreground">Your privacy matters</p>
              <p className="mt-1">
                We'll only use your information to send your renovation preview and
                follow up about your project. No spam, ever.
              </p>
            </div>
          </div>
        </Card>

        {/* Submit Button */}
        <Button
          type="submit"
          className="w-full h-12 text-base font-semibold bg-accent hover:bg-accent/90 text-accent-foreground shadow-md shadow-accent/20 transition-all hover:shadow-lg hover:shadow-accent/25 active:scale-[0.98] disabled:opacity-50 disabled:shadow-none"
          size="lg"
          disabled={isLoading || isCreating}
        >
          {isCreating ? (
            <>
              <Loader2 className="mr-2 h-4 w-4 animate-spin" />
              Creating your preview...
            </>
          ) : (
            'Generate My Renovation Preview'
          )}
        </Button>
      </form>
    </div>
  );
}
